import { useState } from 'react'
import { Plus, Layers } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { TagBadge } from '@/components/tags/TagBadge'
import { useUpdateClip } from '@/hooks/useClips'
import { useTags } from '@/hooks/useTags'
import type { Clip } from '@/types/database'
import { useAnalytics } from '@/hooks/useAnalytics'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface ClipTagsSectionProps {
  clip: Clip
  projectId: string
  canEditTags?: boolean
}

export function ClipTagsSection({ clip, projectId, canEditTags = true }: ClipTagsSectionProps) {
  const { data: tags } = useTags(projectId)
  const updateClip = useUpdateClip()
  const [isAdding, setIsAdding] = useState(false)
  const [selectedTag, setSelectedTag] = useState<string>('')
  const { track } = useAnalytics()

  const clipTags = clip.tags || []

  // Get available tags that aren't already applied to this clip
  const availableTags = tags?.filter(
    (tag) => !clipTags.includes(tag.name)
  ) || []

  const handleAddTag = async () => {
    if (!selectedTag) return

    try {
      const newTags = [...clipTags, selectedTag]
      await updateClip.mutateAsync({
        id: clip.id,
        tags: newTags,
      })

      // Track tag added (no tag_name for privacy - may contain spoilers)
      track('tag_added', {
        project_id: projectId,
        entity_type: 'clip',
        entity_id: clip.id,
      })

      setSelectedTag('')
      setIsAdding(false)
    } catch (error) {
      console.error('Failed to add tag:', error)
    }
  }

  const handleRemoveTag = async (tagToRemove: string) => {
    try {
      const newTags = clipTags.filter((tag) => tag !== tagToRemove)
      await updateClip.mutateAsync({
        id: clip.id,
        tags: newTags,
      })

      // Track tag removed (no tag_name for privacy - may contain spoilers)
      track('tag_added', {
        project_id: projectId,
        entity_type: 'clip',
        entity_id: clip.id,
        action: 'removed',
      })
    } catch (error) {
      console.error('Failed to remove tag:', error)
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Tags ({clipTags.length})
          </CardTitle>
          {canEditTags && !isAdding && availableTags.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsAdding(true)}
              className="h-7 gap-1"
            >
              <Plus className="h-3.5 w-3.5" />
              Add
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Tag List */}
        {clipTags.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {clipTags.map((tagName) => {
              const tag = tags?.find((t) => t.name === tagName)
              return (
                <TagBadge
                  key={tagName}
                  tag={tag || tagName}
                  color={tag?.color}
                  onRemove={canEditTags ? () => handleRemoveTag(tagName) : undefined}
                  size="md"
                />
              )
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No tags applied</p>
        )}

        {/* Add Tag Form */}
        {isAdding && (
          <div className="flex items-center gap-2 pt-2 border-t">
            <Select value={selectedTag} onValueChange={setSelectedTag}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="Select a tag..." />
              </SelectTrigger>
              <SelectContent>
                {availableTags.map((tag) => (
                  <SelectItem key={tag.id} value={tag.name}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      />
                      <span>{tag.display_name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={handleAddTag}
              disabled={!selectedTag || updateClip.isPending}
              className="h-8"
            >
              Add
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setIsAdding(false)
                setSelectedTag('')
              }}
              className="h-8"
            >
              Cancel
            </Button>
          </div>
        )}

        {/* No tags available message */}
        {isAdding && availableTags.length === 0 && (
          <p className="text-sm text-muted-foreground pt-2 border-t">
            All available tags are already applied. Create new tags in the project settings.
          </p>
        )}
      </CardContent>
    </Card>
  )
}
